import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { registerUser, loginUser } from '@/api';
import { setToken } from '@/auth/token';
import GoogleButton from '@/pages/auth/GoogleButton';
import { postLoginPath } from '@/platform';
import ThemeToggle from '@/theme/ThemeToggle';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || postLoginPath();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const user = await registerUser(email, password, name);
      const { token, user: u } = await loginUser(email, password);
      setToken(token, u || user);
      navigate(next.startsWith('/') ? next : postLoginPath());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-bg text-textp px-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <form onSubmit={submit} className="w-[360px] bg-surface border border-border rounded-xl p-6 space-y-4">
        <Link to="/" className="text-lg font-semibold flex items-center gap-2">
          <i className="ri-stack-line text-accent" /> Create your account
        </Link>
        {err && <div className="text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{err}</div>}
        <GoogleButton next={next.startsWith('/') ? next : postLoginPath()} label="Sign up with Google" className="w-full" />
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-texts">
          <span className="flex-1 h-px bg-border" /> or <span className="flex-1 h-px bg-border" />
        </div>
        <label className="block text-xs text-texts">Name
          <input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-bg border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" />
        </label>
        <label className="block text-xs text-texts">Email
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-bg border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" />
        </label>
        <label className="block text-xs text-texts">Password
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-bg border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" />
        </label>
        <button disabled={busy} className="w-full flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-sm font-medium px-3 py-2 rounded-md">
          {busy ? <><i className="ri-loader-4-line animate-spin" /> Creating</> : <><i className="ri-user-add-line" /> Register</>}
        </button>
        <div className="text-xs text-texts text-center">Have an account? <Link to={`/login?next=${encodeURIComponent(next)}`} className="text-accent hover:underline">Sign in</Link></div>
        <p className="text-[11px] text-texts text-center leading-relaxed">
          By creating an account you agree to the <Link to="/terms-of-use" className="text-accent hover:underline">Terms of Use</Link> and <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}
